import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api.service';
import ResponsiveTable from '../components/common/ResponsiveTable';

const amount = (value: any) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (value: any) => value == null ? '—' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });
const date = (value: any) => value ? String(value).slice(0, 10) : '—';
const dateTime = (value: any) => value ? new Date(value).toLocaleString() : '—';
const account = (code: any, name: any) => code ? `${code}${name ? ` — ${name}` : ''}` : '—';
const changeDetails = (change: any) => {
  if (change.type === 'StatusChanged') return `PR ${change.status}; inbox ${change.inboxStatus}`;
  if (change.type === 'Reinitiated') return change.reason || 'Reopened for correction';
  if (change.type === 'SAPPostingReview') {
    const after = change.after || {};
    const result = after.sapPostingStatus === 'Posted'
      ? `linked SAP Purchase Request ${after.sapDocNum ?? after.sapDocEntry ?? ''}`.trim()
      : 'marked as not posted';
    return `SAP posting review: ${result}${after.review?.note ? ` — ${after.review.note}` : ''}`;
  }
  const before = change.before || {}, after = change.after || {};
  const fields = Object.keys(after.header || {}).filter(key =>
    JSON.stringify(before.header?.[key]) !== JSON.stringify(after.header?.[key]))
    .map(key => `${key}: ${before.header?.[key] ?? '—'} → ${after.header?.[key] ?? '—'}`);
  const oldLines = before.lines || [], newLines = after.lines || [];
  for (let i = 0; i < Math.max(oldLines.length, newLines.length); i++) {
    const oldLine = oldLines[i], newLine = newLines[i];
    if (!oldLine) { fields.push(`Line ${i + 1} added: ${newLine?.itemdescription || newLine?.itemcode || 'item'}`); continue; }
    if (!newLine) { fields.push(`Line ${i + 1} removed: ${oldLine?.itemdescription || oldLine?.itemcode || 'item'}`); continue; }
    for (const key of Object.keys(newLine)) {
      if (JSON.stringify(oldLine[key]) !== JSON.stringify(newLine[key]))
        fields.push(`Line ${i + 1} ${key}: ${oldLine[key] ?? '—'} → ${newLine[key] ?? '—'}`);
    }
  }
  return fields.join('; ') || 'Draft details updated';
};

const PRDetailReport: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [posted, setPosted] = useState('');
  const [applied, setApplied] = useState({ search: '', from: '', to: '', status: '', posted: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ records: any[]; total: number; pageSize: number }>({ records: [], total: 0, pageSize: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const companyId = Number(localStorage.getItem('selectedCompanyId') || 0);
      const response = await apiService.getPRDetailReport({ companyId: companyId || undefined,
        search: applied.search || undefined, from: applied.from || undefined, to: applied.to || undefined,
        status: applied.status || undefined, posted: applied.posted || undefined, page, pageSize: 10 });
      if (sequence === requestSequence.current)
        setData(response?.data || { records: [], total: 0, pageSize: 10 });
    } catch (e: any) {
      if (sequence === requestSequence.current) {
        setData({ records: [], total: 0, pageSize: 10 });
        setError(e?.response?.data?.message || e?.message || 'Unable to load report.');
      }
    } finally { if (sequence === requestSequence.current) setLoading(false); }
  }, [applied, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const onCompanyChanged = () => {
      setData({ records: [], total: 0, pageSize: 10 });
      setPage(1);
      void load();
    };
    window.addEventListener('company-changed', onCompanyChanged);
    return () => window.removeEventListener('company-changed', onCompanyChanged);
  }, [load]);

  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setApplied({ search, from, to, status, posted });
  };
  const lastPage = Math.max(1, Math.ceil(data.total / data.pageSize));

  return <Box component="section" aria-labelledby="pr-detail-report-heading" sx={{ p: { xs: 1.5, md: 3 } }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
      <Box>
        <Typography component="h1" id="pr-detail-report-heading" variant="h5" fontWeight={700}>PR Detail Report</Typography>
        <Typography color="text.secondary" variant="body2">PR lines, linked purchase orders, SAP status and receipt progress for the selected company. A dash in received quantity means no matching synced GRPO line is available.</Typography>
      </Box>
      <Button variant="outlined" onClick={() => void load()} disabled={loading}>Refresh SAP status</Button>
    </Stack>

    <Paper component="form" onSubmit={applyFilters} sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField size="small" label="PR number or allocation" value={search} onChange={e => setSearch(e.target.value)} inputProps={{ maxLength: 100 }} />
        <TextField size="small" label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="To" type="date" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" select label="PR status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 135 }}>
          <MenuItem value="">All</MenuItem>
          {['Draft', 'Pending', 'Approved', 'Rejected', 'PostedToSAP/PR', 'ConvertedToPO'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="SAP posting" value={posted} onChange={e => setPosted(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="yes">Posted</MenuItem><MenuItem value="no">Not posted</MenuItem><MenuItem value="review">Needs SAP check</MenuItem>
        </TextField>
        <Button type="submit" variant="contained" disabled={loading}>Apply</Button>
      </Stack>
    </Paper>

    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {loading && <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={28} /></Stack>}
    {!loading && <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{data.total} PRs found · page {page} of {lastPage}</Typography>
      {data.records.length === 0 && <Alert severity="info">No PRs match these filters and your access privileges.</Alert>}
      <Stack spacing={2}>
        {data.records.map((pr: any) => <Paper key={pr.prId} sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight={700}>{pr.prNumber}</Typography>
                <Chip size="small" label={pr.status || 'Unknown'} />
                {pr.postingStatus && <Chip size="small" color={pr.postingStatus === 'Failed' ? 'error' : ['Unknown', 'Posting'].includes(pr.postingStatus) ? 'warning' : 'default'} label={`SAP: ${pr.postingStatus}`} />}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {pr.companyName || ''} · {pr.requester || ''} · {date(pr.prDate)} · {pr.requisitionType} · {pr.allocationType}: {pr.allocationCode}
              </Typography>
              <Typography variant="body2">Required {date(pr.requiredDate)} · PR total {amount(pr.total)} {pr.currency || ''} · SAP PR #{pr.sapPRDocNum || '—'} · {pr.purchaseOrders.length} linked PO(s)</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Button size="small" onClick={() => navigate(`/pr/${pr.prId}`)}>Open PR</Button>
              <Button size="small" onClick={() => setExpanded(current => ({ ...current, [pr.prId]: !current[pr.prId] }))} aria-expanded={!!expanded[pr.prId]}>
                {expanded[pr.prId] ? 'Hide details' : 'Show details'}
              </Button>
            </Stack>
          </Stack>
          {expanded[pr.prId] && <Box sx={{ mt: 2 }}>
            {pr.postingError && <Alert severity="warning" sx={{ mb: 2 }}>{pr.postingError}</Alert>}
            <Typography variant="body2" sx={{ mb: 1 }}>Approval flow: {pr.approvalFlow || '—'} · Current step: {pr.currentStep ?? '—'} · SAP posting attempted: {dateTime(pr.postingAttemptedAt)}</Typography>
            {pr.remarks && <Typography variant="body2" sx={{ mb: 1 }}>Remarks: {pr.remarks}</Typography>}
            {pr.rejectionReason && <Typography variant="body2" sx={{ mb: 1 }}>Rejection reason: {pr.rejectionReason}</Typography>}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PR lines</Typography>
            <ResponsiveTable ariaLabel={`Lines for PR ${pr.prNumber}`} rows={pr.lines} getRowKey={(line: any) => line.lineNum}
              columns={[
                { id: 'line', header: '#', cell: (line: any) => line.lineNum },
                { id: 'item', header: 'Item / description', cell: (line: any) => `${line.itemCode || 'Service'} — ${line.description || ''}` },
                { id: 'lineRemarks', header: 'Line remarks', cell: (line: any) => line.lineRemarks || '—' },
                { id: 'quantity', header: 'PR qty', cell: (line: any) => qty(line.quantity), align: 'right' },
                { id: 'open', header: 'PR open qty', cell: (line: any) => qty(line.openQty), align: 'right' },
                { id: 'closed', header: 'PR closed qty', cell: (line: any) => qty(line.closedQty), align: 'right' },
                { id: 'price', header: 'Unit price', cell: (line: any) => amount(line.unitPrice), align: 'right' },
                { id: 'total', header: 'Line total', cell: (line: any) => amount(line.lineTotal), align: 'right' },
                { id: 'vendor', header: 'Vendor', cell: (line: any) => line.vendorCode || '—' },
                { id: 'project', header: 'Line project', cell: (line: any) => line.projectCode || '—' },
                { id: 'dimensions', header: 'Division / Dept / WBS / Aux / LOC', cell: (line: any) =>
                  [line.dim1, line.dim2, line.dim3, line.dim4, line.dim5].map((value: any) => value || '—').join(' / ') },
                { id: 'service', header: 'Service qty / price', cell: (line: any) => line.serviceQty == null ? '—' : `${qty(line.serviceQty)} / ${amount(line.serviceUnitPrice)}` },
              ]} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PR account coding and GL rule reference</Typography>
            <Alert severity="info" sx={{ mb: 1 }}>PR and PO values are planned amounts. The advanced rule is the Auto Invoice cost account reference; SAP may post a different account. Only linked SAP journal entries below show actual debit and credit.</Alert>
            <ResponsiveTable ariaLabel={`Account coding for PR ${pr.prNumber}`} rows={pr.lines} getRowKey={(line: any) => line.lineNum}
              columns={[
                { id: 'line', header: 'PR line', cell: (line: any) => line.lineNum },
                { id: 'planned', header: 'Planned amount', cell: (line: any) => amount(line.lineTotal), align: 'right' },
                { id: 'account', header: 'PR account', cell: (line: any) => account(line.accountCode, line.accountName) },
                { id: 'rule', header: 'Auto Invoice cost rule reference', cell: (line: any) => line.advancedCostRule ? `${account(line.advancedCostRule.accountCode, line.advancedCostRule.accountName)} (${line.advancedCostRule.rule})` : '—' },
              ]} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Linked purchase orders</Typography>
            {pr.purchaseOrders.length === 0 && <Alert severity="info">No linked SAP purchase order has been recorded for this PR.</Alert>}
            <Stack spacing={2}>
              {pr.purchaseOrders.map((po: any, index: number) => <Paper key={`${po.poId || po.sapDocEntry || index}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography fontWeight={700}>PO {po.sapDocNum || po.poNumber || 'Pending'} <Chip size="small" sx={{ ml: 1 }} label={po.sapStatus || po.localStatus || 'Not posted'} /></Typography>
                    <Typography variant="body2">SAP DocEntry {po.sapDocEntry || '—'} · Created {date(po.createdDate)} · Vendor {po.vendorName || po.vendorCode || '—'}</Typography>
                    <Typography variant="body2">PO total {amount(po.total)} {po.currency || ''} · Local status {po.localStatus || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">SAP source: {po.sapSource || 'unavailable'} {po.sapAsOf ? `as of ${dateTime(po.sapAsOf)}` : ''} · PO lines: {po.linesSource || 'unavailable'} · Latest synced GRPO {po.receiptsAsOf ? dateTime(po.receiptsAsOf) : 'not available'}</Typography>
                  </Box>
                  {po.poId && <Button size="small" onClick={() => navigate(`/po/${po.poId}`)}>Open PO</Button>}
                </Stack>
                {po.sapError && <Alert severity="warning" sx={{ mt: 1 }}>{po.sapError}</Alert>}
                <Box sx={{ mt: 1.5 }}>
                  <ResponsiveTable ariaLabel={`Lines for PO ${po.sapDocNum || po.poNumber || index + 1}`} rows={po.lines} getRowKey={(line: any, i: number) => `${line.lineNum}-${i}`}
                    emptyMessage={po.sapDocEntry ? 'SAP PO lines unavailable.' : 'PO has not been posted to SAP.'}
                    columns={[
                      { id: 'line', header: '#', cell: (line: any) => line.lineNum + 1 },
                      { id: 'source', header: 'PR line', cell: (line: any) => line.sourcePRLineNum ?? '—' },
                      { id: 'item', header: 'Item / description', cell: (line: any) => `${line.itemCode || 'Service'} — ${line.description || ''}` },
                      { id: 'ordered', header: 'Ordered qty', cell: (line: any) => qty(line.quantity), align: 'right' },
                      { id: 'received', header: 'Received qty', cell: (line: any) => qty(line.receivedQty), align: 'right' },
                      { id: 'open', header: 'Open qty', cell: (line: any) => qty(line.openQty), align: 'right' },
                      { id: 'closed', header: 'Closed without receipt', cell: (line: any) => qty(line.closedQty), align: 'right' },
                      { id: 'remaining', header: 'Remaining qty', cell: (line: any) => qty(line.remainingQty), align: 'right' },
                      { id: 'price', header: 'Unit price', cell: (line: any) => amount(line.unitPrice), align: 'right' },
                      { id: 'total', header: 'Line total', cell: (line: any) => amount(line.lineTotal), align: 'right' },
                      { id: 'account', header: 'PO account', cell: (line: any) => account(line.accountCode, line.accountName) },
                      { id: 'status', header: 'Line status', cell: (line: any) => line.lineStatus },
                    ]} />
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Linked GRPO journal impact</Typography>
                {po.journalEntries?.length ? po.journalEntries.map((entry: any) =>
                  <Box key={entry.journalId} sx={{ mt: 1 }}>
                    <Typography variant="body2">SAP journal {entry.number || entry.journalId} · {date(entry.date)} · Synced {dateTime(entry.asOf)}</Typography>
                    <Typography variant="caption" color="text.secondary">Whole GRPO journal; it may also include lines from other POs. Amounts are not allocated to this PR.</Typography>
                    <ResponsiveTable ariaLabel={`Journal ${entry.journalId} GL lines`} rows={entry.lines || []}
                      getRowKey={(line: any, i: number) => `${line.accountCode}-${i}`}
                      columns={[
                        { id: 'account', header: 'GL account', cell: (line: any) => account(line.accountCode, line.accountName) },
                        { id: 'debit', header: 'Debit', cell: (line: any) => amount(line.debit), align: 'right' },
                        { id: 'credit', header: 'Credit', cell: (line: any) => amount(line.credit), align: 'right' },
                        { id: 'memo', header: 'Memo', cell: (line: any) => line.memo || '—' },
                      ]} />
                  </Box>) : <Typography variant="body2" color="text.secondary">No verified linked journal in the synced SAP data.</Typography>}
              </Paper>)}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PR detail changes</Typography>
            <ResponsiveTable ariaLabel={`Changes for PR ${pr.prNumber}`} rows={pr.changes || []}
              getRowKey={(entry: any, index: number) => `${entry.type}-${entry.at}-${index}`}
              emptyMessage="No tracked detail changes yet. Changes made before tracking was enabled are unavailable."
              columns={[
                { id: 'at', header: 'Date', cell: (entry: any) => dateTime(entry.at) },
                { id: 'actor', header: 'Changed by', cell: (entry: any) => entry.actor || '—' },
                { id: 'type', header: 'Change', cell: (entry: any) => entry.type },
                { id: 'details', header: 'Details', cell: (entry: any) => changeDetails(entry) },
              ]} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Approval history</Typography>
            <ResponsiveTable ariaLabel={`Approval history for PR ${pr.prNumber}`} rows={pr.approvalHistory || []}
              getRowKey={(entry: any, index: number) => `${entry.step}-${entry.at}-${index}`}
              columns={[
                { id: 'step', header: 'Step', cell: (entry: any) => entry.step },
                { id: 'stage', header: 'Stage', cell: (entry: any) => entry.stage || '—' },
                { id: 'approver', header: 'Approver', cell: (entry: any) => entry.approver || '—' },
                { id: 'action', header: 'Action', cell: (entry: any) => entry.action || '—' },
                { id: 'at', header: 'Date', cell: (entry: any) => dateTime(entry.at) },
                { id: 'comments', header: 'Comments', cell: (entry: any) => entry.comments || '—' },
              ]} />
          </Box>}
        </Paper>)}
      </Stack>
      {data.total > data.pageSize && <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
        <Button disabled={page <= 1} onClick={() => setPage(current => current - 1)}>Previous</Button>
        <Button disabled={page >= lastPage} onClick={() => setPage(current => current + 1)}>Next</Button>
      </Stack>}
    </>}
  </Box>;
};

export default PRDetailReport;
